import { useParams } from 'react-router-dom';
import { Padding } from 'Components/Padding';
import Breadcrumb from 'Components/Breadcrumb/Breadcrumb';
import CertificateDownload from 'Components/CertificateDownload/CertificateDownload';
import CourseStatusChip from 'Components/CourseStatusChip';
import GenericTableText from 'Components/GenericTableText/GenericTableText';
import SideBarContainer from 'Containers/SideBarContainer';
import { useHistoryEmployeeReportPage } from 'Hooks/useHistoryEmployeeReportPage';
import ReportsLayout from 'Pages/reports/components/ReportsLayout/ReportsLayout';
import ReportsTitle from 'Pages/reports/components/ReportsTitle/ReportsTitle';
import ReportsDataGrid from 'Pages/reports/components/ReportsDataGrid/ReportsDataGrid';
import { ReportsButtonGroup } from 'Pages/reports/components/ReportsButtonGroup/ReportsButtonGroup';
import { formatDate } from 'Utils/format-date';
import { toReportsPagination } from 'Pages/reports/utils/pagination';
import { HistoryEmployeeReportFilter } from './components/HistoryEmployeeReportFilter/HistoryEmployeeReportFilter';
import TotalEmployeeHistoryDetails from './components/TotalEmployeeHistoryDetails/TotalEmployeeHistoryDetails';
import { CoursesReportIcon, EmployeesReportIcon } from 'Pages/reports/components/icons';
import type { GridColDef } from '@mui/x-data-grid';

interface Params {
  employeeId: string;
}

interface Row {
  id: string;
  courseId: string;
  name: string;
  priorityName: string;
  categoryName: string;
  statusId: string;
  statusName: string;
  assignedDate: string;
  finishedAtRaw: string | null;
  certificationId: string | null;
}

export default function Page() {
  const { employeeId } = useParams<Params>();

  const {
    t2,
    data,
    pagination,
    totals,
    loading,
    error,
    employee,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleExport,
    handleDownloadCertificate,
    handleRowClick,
  } = useHistoryEmployeeReportPage(employeeId);

  const buttons = [
    {
      icon: <EmployeesReportIcon />,
      tooltip: t2('employees_report_tooltip'),
      route: '/reporte/historial/empleados',
    },
    { icon: <CoursesReportIcon />, tooltip: t2('courses_report_tooltip'), route: '/reporte/historial/cursos' },
  ];

  const columns: GridColDef[] = [
    { field: 'name', headerName: t2('course_name'), flex: 1.5 },
    { field: 'priorityName', headerName: t2('priority'), flex: 1 },
    { field: 'categoryName', headerName: t2('category'), flex: 1 },
    { field: 'assignedDate', headerName: t2('assigned_date'), flex: 1 },
    {
      field: 'finishedAt',
      headerName: t2('finished_date'),
      flex: 1,
      renderCell: params =>
        params.row.finishedAtRaw ? (
          formatDate(params.row.finishedAtRaw)
        ) : (
          <GenericTableText text={t2('not_finished')} />
        ),
    },
    {
      field: 'statusName',
      headerName: t2('status'),
      flex: 1.2,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => <CourseStatusChip statusId={params.row.statusId} status={params.row.statusName} />,
    },
    {
      field: 'downloadCertificate',
      headerName: t2('download_certificate'),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: params => (
        <CertificateDownload
          certificationId={params.row.certificationId}
          onDownload={() => handleDownloadCertificate(params.row.id, params.row.certificationId ?? '')}
        />
      ),
    },
  ];

  const rows: Row[] = data.map(course => ({
    id: course.id,
    courseId: course.courseId,
    name: course.name,
    priorityName: course.priority?.name || '-',
    categoryName: course.category?.name || '-',
    statusId: course.status?.id || '',
    statusName: course.status?.name || '-',
    assignedDate: course.createdAt ? formatDate(course.createdAt) : '-',
    finishedAtRaw: course.finishedAt ?? null,
    certificationId: course.certificationId,
  }));

  return (
    <SideBarContainer>
      <Padding padding="1rem 1rem 0 1rem">
        <Breadcrumb
          items={[
            { label: t2('history_reports'), path: '/reporte/historial/empleados' },
            { label: t2('employee_detail'), path: `/reporte/historial/cursos` },
          ]}
        />
      </Padding>
      <ReportsLayout
        title={<ReportsTitle title={t2('title')} accent={`"${employee?.fullName || ''}"`} />}
        buttons={<ReportsButtonGroup buttons={buttons} />}
        onExport={handleExport}
      />
      <Padding padding="1rem">
        <TotalEmployeeHistoryDetails totals={totals} />
        <HistoryEmployeeReportFilter onSearch={handleSearch} />
        <Padding padding="0.5rem 0" />
        <ReportsDataGrid<Row>
          columns={columns}
          rows={rows}
          loading={loading}
          error={error}
          pagination={toReportsPagination(pagination)}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onRowClick={row => handleRowClick({ courseId: row.courseId })}
          noDataMessage={t2('no_data')}
        />
      </Padding>
    </SideBarContainer>
  );
}
